import { Avatar, Button, Header, Loading, Text } from 'components';
import { PAGE_SIZE, TEXT_STRING, queryKeys, routes } from '../../../constants';
import { useMemo } from 'react';
import { formatDate } from 'utils';
import { useInfiniteQuery } from '@tanstack/react-query';
import { getFemalesMet } from 'api';
import InfiniteScroll from 'react-infinite-scroll-component';
import { TFemaleMet } from 'types';
import { useNavigate } from 'react-router-dom';

const STR = TEXT_STRING.MY_PAGE;
const COMMON_STR = TEXT_STRING.COMMON;

function FemaleMet() {
  // State
  const navigate = useNavigate();

  // Query
  const { data, isLoading, fetchNextPage, hasNextPage } = useInfiniteQuery({
    queryKey: [queryKeys.FEMALES_MET],
    queryFn: async ({ pageParam }) => getFemalesMet(pageParam, PAGE_SIZE),
    initialPageParam: 1,
    getNextPageParam: (lastPage, _, currentPage) => {
      return lastPage?.totalPage > currentPage ? currentPage + 1 : undefined;
    },
  });

  const listView = useMemo(
    () => data?.pages.map(({ data }) => data).flat(),
    [data?.pages],
  );

  const listViewFiltered = useMemo(() => {
    const groupedData: {
      date: string;
      area: { id: number; name: string };
      data: TFemaleMet[];
    }[] = [];

    listView?.forEach((item) => {
      const dateTime = new Date(item.startTime).toDateString();

      let group = groupedData.find(
        (g) => g.date === dateTime && g.area.id === item.area?.id,
      );

      if (!group) {
        group = {
          date: dateTime,
          area: { id: item.area?.id, name: item.area?.name },
          data: [],
        };
        groupedData.push(group);
      }

      group.data.push(item);
    });

    return groupedData;
  }, [listView]);

  if (isLoading) {
    return <Loading fullScreen />;
  }

  return (
    <div className="h-full w-full overflow-y-auto" id="scrollFemaleMet">
      <Header
        title={
          <Text fontSize={14} bold>
            {STR.FEMALE_MET_TEXT}
          </Text>
        }
      />
      <InfiniteScroll
        next={fetchNextPage}
        hasMore={hasNextPage}
        loader={
          <div className="p-4 bg-neutral-25 flex items-center gap-4">
            <div className="w-24 h-24 rounded-full animate-pulse bg-gray-400"></div>
            <div className="flex-1 h-10 animate-pulse bg-gray-400"></div>
          </div>
        }
        dataLength={listView?.length || 0}
        scrollThreshold={0.5}
        scrollableTarget="scrollFemaleMet"
        className="flex flex-col gap-8"
      >
        {listViewFiltered?.length ? (
          listViewFiltered.map((item, index) => {
            return (
              <div className="w-full mt-5" key={index}>
                <Text className="mb-2 ml-4">{`${formatDate(
                  item.date,
                  'LL',
                )} - ${item?.area?.name || ''}`}</Text>
                {item.data?.map((female) => {
                  return (
                    <div
                      className="bg-neutral-25 flex gap-2 items-center justify-between w-full p-4"
                      key={female.femaleId}
                    >
                      <div
                        className={`flex flex-1 items-center gap-4 ${
                          female.inactive ? 'opacity-50' : ''
                        }`}
                      >
                        <Avatar url={female.avatar} size="w-24" circle />
                        <Text className="line-clamp-2">{female.nickname}</Text>
                      </div>

                      <Button
                        onClick={() =>
                          navigate(
                            `${routes.DETAIL_FEMALE.replace(
                              ':id',
                              `${female?.femaleId}`,
                            )}`,
                          )
                        }
                        textColor={'text-white'}
                        border="border"
                        bold
                      >
                        {COMMON_STR.GO_TO_PROFILE}
                      </Button>
                    </div>
                  );
                })}
              </div>
            );
          })
        ) : (
          <Text bold center className="py-16">
            {TEXT_STRING.COMMON.NO_DATA}
          </Text>
        )}
      </InfiniteScroll>
    </div>
  );
}

export default FemaleMet;
